import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useLang, CONTACT } from "../../translations";

const links = ["about", "expertise", "journey", "location", "faq"];

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-cream/80 backdrop-blur-xl border-b border-ink/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("hero")} className="flex flex-col items-start leading-none">
          <span className="font-heading text-xl md:text-2xl text-ink tracking-tight">Sushma Harsh Timmaraju</span>
          <span className="text-[10px] uppercase tracking-[0.28em] text-sage mt-0.5">Homeopathie Classique</span>
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l}
              data-testid={`nav-${l}`}
              onClick={() => go(l)}
              className="text-sm text-muted2 hover:text-sage transition-colors duration-300 relative group"
            >
              {t.nav[l]}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-sage transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-ink/15 p-0.5 text-xs">
            {["fr", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-toggle-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 rounded-full uppercase tracking-wider transition-colors duration-300 ${
                  lang === l ? "bg-sage text-cream" : "text-muted2 hover:text-ink"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-book-cta"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-ink text-cream px-5 py-2.5 text-sm hover:bg-sage transition-colors duration-300"
          >
            <Phone size={15} /> {t.nav.book}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden h-11 w-11 flex items-center justify-center rounded-full border border-ink/15 text-ink"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-t border-ink/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {[...links, "contact"].map((l) => (
                <button
                  key={l}
                  data-testid={`mobile-nav-${l}`}
                  onClick={() => go(l)}
                  className="text-left font-heading text-2xl text-ink py-2 border-b border-ink/5"
                >
                  {t.nav[l] || t.nav.contact}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
